/**
 * Login page -- email + password against /api/auth/login via AuthContext.
 */
import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardHeader, CardBody, CardFooter } from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import { Button } from "../../components/ui/Button";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/";

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login({ email, password });
      navigate(redirect, { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-12 md:py-20">
      <div className="flex justify-center mb-5" data-testid="login-logo">
        <img
          src="/logo.png"
          alt="Parts Buoy"
          className="h-16 w-16 md:h-20 md:w-20 object-contain"
        />
      </div>
      <Card elevated data-testid="login-card">
        <CardHeader>
          <div>
            <h1 className="text-3xl font-black mb-1">Welcome back</h1>
            <p className="text-ink-muted text-sm">Sign in to your Parts Buoy account.</p>
          </div>
        </CardHeader>
        <CardBody>
          <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
            <Input
              label="Email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
            />
            <Input
              label="Password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
            />
            {error && (
              <div
                role="alert"
                data-testid="login-error"
                className="rounded-xl border-2 border-brand bg-brand-softer text-brand-ink px-3 py-2 text-sm font-medium"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={loading}
              data-testid="login-submit"
            >
              Sign in
            </Button>
            <div className="text-center">
              <Link
                to="/forgot-password"
                className="text-sm font-semibold text-ink-muted hover:text-brand"
                data-testid="login-forgot-link"
              >
                Forgot your password?
              </Link>
            </div>
          </form>
        </CardBody>
        <CardFooter className="justify-between text-sm">
          <span className="text-ink-muted">New here?</span>
          <Link
            to="/register"
            className="font-semibold text-brand hover:text-brand-hover"
            data-testid="login-register-link"
          >
            Create an account →
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
}
